<template>
	<van-popup
		:show="show"
		position="left"
		:class="[
			'm-user-center-drawer-popup',
			{ 'm-user-center-drawer-popup--en': currentLocale === 'en' },
		]"
		overlay-class="m-user-center-drawer-overlay"
		:style="popupStyle"
		:overlay-style="overlayStyle"
		:z-index="2300"
		@update:show="onPopupShowChange"
	>
		<div class="user-center-drawer">
			<div class="drawer-scroll">
				<div class="drawer-main">
					<div class="user-profile">
						<van-image class="avatar" :src="userInfo?.avatar" fit="cover" round />
						<div class="profile-info">
							<div class="name-row">
								<p class="nickname">{{ userInfo?.nickname || "..." }}</p>
								<span class="member-badge">{{ memberLabel }}</span>
							</div>
							<p class="greeting">{{ greeting }}</p>
						</div>
					</div>

					<div class="wallet-card" @click="navigate('wallet')">
						<div class="wallet-title">
							<span>{{ t("user.pc.sidebar.wallet") }}</span>
							<SidebarIcon name="wallet-arrow" class="wallet-arrow-icon" />
						</div>
						<p class="wallet-balance">{{ formatBalance(userInfo?.coin_num) }}</p>
					</div>

					<nav class="menu-list" :aria-label="t('mHeader.drawer.userCenter')">
						<div
							v-for="item in menuItems"
							:key="item.key"
							class="menu-item"
							:class="{ active: item.key === activeKey }"
							@click="handleMenuClick(item)"
						>
							<div v-if="item.key === activeKey" class="active-bar" aria-hidden="true" />
							<SidebarIcon v-if="item.icon" :name="item.icon" class="menu-icon" />
							<img v-else class="menu-icon-img" :src="item.iconSrc" alt="" aria-hidden="true" />
							<span class="menu-label">{{ item.label }}</span>
							<span v-if="item.badge && item.badge > 0" class="menu-badge">
								{{ item.badge > 99 ? "99+" : item.badge }}
							</span>
							<div
								v-if="item.action === 'language'"
								class="drawer-lang"
								role="group"
								aria-label="語言"
								@click.stop
							>
								<button
									type="button"
									class="drawer-lang-btn"
									:class="{ 'drawer-lang-btn--active': currentLocale === 'zh-TW' }"
									@click="setDrawerLocale('zh-TW')"
								>
									繁
								</button>
								<button
									type="button"
									class="drawer-lang-btn"
									:class="{ 'drawer-lang-btn--active': currentLocale === 'en' }"
									@click="setDrawerLocale('en')"
								>
									EN
								</button>
							</div>
						</div>
					</nav>
				</div>
			</div>

			<div class="drawer-shortcuts">
				<div class="drawer-shortcuts-row">
					<button
						v-for="item in shortcutItems"
						:key="item.key"
						type="button"
						class="drawer-shortcut"
						@click="handleShortcutClick(item)"
					>
						<img class="drawer-shortcut__icon" :src="item.icon" alt="" aria-hidden="true" />
						<span>{{ item.label }}</span>
					</button>
				</div>
				<p class="drawer-version">V4.2.0</p>
			</div>
		</div>
	</van-popup>
</template>

<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { showConfirmDialog } from "vant";
import { useRoute } from "vue-router";
import { useUserStore } from "~/stores/user";
import { useMsgCountStore } from "~/stores/msg-count";
import { downloadApp, parseUA } from "~/utils/common";
import { redirectToKefu } from "~/utils/kefu";
import { useI18n } from "~/composables/useI18n";
import { logEvent } from "~/utils/firebase";
import { mapTrackingLanguage } from "~/composables/useSiteComponentTracking";
import { useTaiwanIp } from "~/composables/useTaiwanIp";
import { useUserCenterTracking } from "@variants/seer/user/composables/useUserCenterTracking";
import SidebarIcon from "@variants/seer/user/pc/components/Sidebar/SidebarIcon.vue";
import iconKefu from "~/components/m/header/images/icon_kefu.svg";
import iconMentor from "~/components/m/header/images/icon_mentor.svg";
import iconInstitution from "~/components/m/header/images/icon_institution.svg";
import iconDownload from "~/components/m/header/images/icon_download.svg";
import iconLanguage from "~/components/m/header/images/icon_language.svg";

const props = defineProps({
	show: {
		type: Boolean,
		default: false,
	},
	topOffset: {
		type: Number,
		default: 0,
	},
});

const emit = defineEmits(["update:show", "back"]);

const CLOSE_MS = 320;

const route = useRoute();
const { t, locale, setLocale } = useI18n();
const useUser = useUserStore();
const { userInfo } = storeToRefs(useUser);
const { unReadMsgTotalCount } = storeToRefs(useMsgCountStore());
const { track, trackNavClick, trackCustomerSupportClick } = useUserCenterTracking("mobile");
const { checkTaiwanIp } = useTaiwanIp();
const currentLocale = computed(() => locale?.value || "zh-TW");

const popupStyle = computed(() => {
	return {
		width: "72%",
		top: "0",
		height: "100%",
	};
});

const overlayStyle = computed(() => {
	return {
		background: "rgba(0, 0, 0, 0.7)",
		top: "0",
		height: "100%",
	};
});

const memberLabel = computed(() =>
	userInfo.value?.is_monthly_vip
		? t("user.pc.sidebar.monthlyMember")
		: t("user.pc.sidebar.normalMember")
);

const greeting = computed(() => {
	const hour = new Date().getHours();
	if (hour >= 6 && hour < 12) return t("user.pc.sidebar.goodMorning");
	if (hour >= 12 && hour < 14) return t("user.pc.sidebar.goodNoon");
	if (hour >= 14 && hour < 18) return t("user.pc.sidebar.goodAfternoon");
	if (hour >= 18 && hour < 24) return t("user.pc.sidebar.goodEvening");
	return t("user.pc.sidebar.goodNight");
});

const activeKey = computed(() => {
	const type = route.query.type || "default";
	const typeMap = {
		default: "file",
		wallet: "wallet",
		order: "order",
		follow: "follow",
		message: "message",
		coupon: "coupon",
		address: "address",
		account: "account",
	};
	return route.path.startsWith("/seer/user") ? typeMap[type] || "file" : "";
});

const menuItems = computed(() => [
	{ key: "order", label: t("user.pc.order.title"), icon: "order" },
	{ key: "follow", label: t("user.pc.follow.title"), icon: "follow" },
	{
		key: "message",
		label: t("user.pc.message.title"),
		icon: "message",
		badge: unReadMsgTotalCount.value,
	},
	{ key: "coupon", label: t("user.pc.sidebar.coupon"), icon: "coupon" },
	{ key: "file", label: t("user.pc.default.title"), icon: "file" },
	{ key: "address", label: t("user.pc.sidebar.address"), icon: "address" },
	{ key: "account", label: t("user.pc.sidebar.account"), icon: "account" },
	{
		key: "download",
		label: t("mHeader.drawer.downloadApp"),
		action: "download",
		iconSrc: iconDownload,
	},
	{
		key: "language",
		label: t("mHeader.drawer.languageSwitch"),
		action: "language",
		iconSrc: iconLanguage,
	},
	{ key: "logout", label: t("user.pc.sidebar.logout"), icon: "logout", action: "logout" },
]);

const shortcutItems = computed(() => [
	{
		key: "contact-cs",
		label: t("mHeader.drawer.contactCs"),
		path: "/seer/onlinecs",
		icon: iconKefu,
	},
	{
		key: "mentor-join",
		label: t("mHeader.drawer.mentorJoin"),
		path: "/seer/mentor-join",
		icon: iconMentor,
	},
	{
		key: "institution-join",
		label: t("mHeader.drawer.institutionJoin"),
		path: "/seer/corporate-center",
		icon: iconInstitution,
	},
]);

const formatBalance = value => {
	const num = Number(value);
	if (Number.isNaN(num)) return "...";
	return num.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const onPopupShowChange = value => {
	emit("update:show", value);
};

const closeAsync = () =>
	new Promise(resolve => {
		if (!props.show) {
			resolve();
			return;
		}
		emit("update:show", false);
		setTimeout(resolve, CLOSE_MS);
	});

const routeMap = {
	order: "order",
	follow: "follow",
	message: "message",
	coupon: "coupon",
	file: "default",
	wallet: "wallet",
	address: "address",
	account: "account",
};

const firebaseEventMap = {
	wallet: "site_profile_wallet_click",
	order: "site_profile_order_click",
	follow: "site_profile_follow_click",
	message: "site_profile_message_click",
	coupon: "site_profile_coupon_click",
	file: "site_profile_default_click",
	address: "site_profile_address_click",
	account: "site_profile_account_click",
};

const navigate = async key => {
	const type = routeMap[key];
	if (!type) return;

	firebaseEventMap[key] && logEvent(firebaseEventMap[key]);
	trackNavClick(key, {
		unread_count: key === "message" ? unReadMsgTotalCount.value : undefined,
	});
	await closeAsync();
	await navigateTo({
		path: "/seer/user",
		query: { type },
	});
};

const handleMenuClick = async item => {
	if (item.action === "language") return;
	if (item.action === "download") {
		const res = parseUA();
		logEvent(res.android ? "site_googleplay_click" : "site_appstore_click");
		track("app_download_click", {
			element_id: "download_app_top",
			store_type: res.android ? "google_play" : res.ios ? "app_store" : "unknown",
		});
		await closeAsync();
		if (await checkTaiwanIp()) {
			await navigateTo("/seer/app-landing");
			return;
		}
		downloadApp();
		return;
	}
	if (item.action === "logout") {
		trackNavClick("logout");
		showConfirmDialog({
			className: "seer-logout-dialog",
			title: t("user.pc.logout.title"),
			confirmButtonText: t("user.pc.logout.confirmButton"),
			cancelButtonText: t("user.pc.common.cancel"),
			confirmButtonColor: "#111111",
			closeOnClickOverlay: true,
			zIndex: 2400,
		})
			.then(async () => {
				await closeAsync();
				useUser.exit();
			})
			.catch(() => {});
		return;
	}
	await navigate(item.key);
};

const setDrawerLocale = next => {
	if (currentLocale.value === next) return;
	track("language_switch_click", {
		from_language: mapTrackingLanguage(currentLocale.value),
		to_language: mapTrackingLanguage(next),
	});
	setLocale(next);
};

const handleShortcutClick = async item => {
	await closeAsync();
	if (item.key === "contact-cs") {
		trackCustomerSupportClick({
			trigger_position: "mobile_user_drawer_shortcut",
		});
		redirectToKefu();
		return;
	}
	if (item.key === "mentor-join") {
		trackNavClick("mentor_join", {
			nav_item: "mentor_join",
		});
	}
	if (item.key === "institution-join") {
		trackNavClick("institution_join", {
			nav_item: "institution_join",
		});
	}
	await navigateTo({ path: item.path });
};
</script>

<style lang="scss" scoped>
.user-center-drawer {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: #ffffff;
	font-family: "Source Han Serif CN", "PingFang TC", serif;
	overflow: hidden;
}

.drawer-scroll {
	flex: 1;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.drawer-main {
	display: flex;
	flex-direction: column;
	gap: 28px;
	padding: 50px 32px 0;
}

.user-profile {
	display: flex;
	align-items: center;
	gap: 16px;

	.avatar {
		width: 84px;
		height: 84px;
		flex-shrink: 0;
	}

	.profile-info {
		min-width: 0;
		flex: 1;
	}

	.name-row {
		display: flex;
		align-items: center;
		gap: 16px;
		flex-wrap: wrap;
	}

	.nickname {
		font-weight: 700;
		font-size: 36px;
		color: #111111;
		line-height: 48px;
	}

	.member-badge {
		display: inline-flex;
		align-items: center;
		height: 44px;
		padding: 0 18px;
		background: #ffe7cc;
		border-radius: 22px;
		font-weight: 700;
		font-size: 28px;
		color: #944402;
		line-height: 36px;
		white-space: nowrap;
	}

	.greeting {
		margin-top: 12px;
		font-size: 24px;
		color: #999999;
		line-height: 28px;
	}
}

.wallet-card {
	position: relative;
	height: 160px;
	padding: 24px;
	border-radius: 24px;
	background: linear-gradient(180deg, #ffea94 0%, #fffcea 60%);
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	.wallet-title {
		display: flex;
		align-items: center;
		gap: 6px;
		font-weight: 900;
		font-size: 32px;
		color: #663600;
		line-height: 40px;
	}

	.wallet-arrow-icon :deep(.sidebar-icon) {
		width: 28px !important;
		height: 28px !important;
	}

	.wallet-balance {
		margin-top: 16px;
		font-family: "DIN Alternate", "D-DIN Exp", sans-serif;
		font-weight: 700;
		font-size: 48px;
		color: #de2828;
		line-height: 48px;
		letter-spacing: -0.6px;
	}
}

.menu-list {
	display: flex;
	flex-direction: column;
	gap: 34px;
	padding-bottom: 32px;
}

.menu-item {
	position: relative;
	display: flex;
	align-items: center;
	gap: 24px;
	min-height: 40px;
	font-size: 28px;
	font-weight: 400;
	color: #111111;
	line-height: 28px;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	&.active {
		margin: 0 -32px;
		padding: 18px 32px;
		min-height: 84px;
		background: linear-gradient(90deg, #fff6e4 0%, #ffffff 100%);
	}

	.active-bar {
		position: absolute;
		left: 0;
		top: 0;
		width: 8px;
		height: 84px;
		background: #f2af19;
		border-radius: 62px;
	}

	.menu-icon :deep(.sidebar-icon) {
		width: 40px !important;
		height: 40px !important;
	}

	.menu-icon-img {
		width: 40px;
		height: 40px;
		flex-shrink: 0;
	}

	.menu-label {
		flex: 1;
		min-width: 0;
	}

	.menu-badge {
		flex-shrink: 0;
		min-width: 36px;
		height: 36px;
		padding: 0 10px;
		background: #de2828;
		border: 1px solid #ffffff;
		border-radius: 36px;
		font-family: "PingFang SC", sans-serif;
		font-weight: 600;
		font-size: 24px;
		color: #ffffff;
		line-height: 36px;
		text-align: center;
	}
}

.drawer-lang {
	display: flex;
	width: 100px;
	height: 48px;
	border-radius: 8px;
	background: #e2e2e2;
	overflow: hidden;
	flex-shrink: 0;
}

.drawer-lang-btn {
	flex: 1;
	border: none;
	background: transparent;
	padding: 0;
	font-family: "Source Han Sans CN", sans-serif;
	font-size: 24px;
	font-weight: 500;
	line-height: 28px;
	color: #ffffff;
	white-space: nowrap;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	&--active {
		background: #333333;
	}
}

.drawer-shortcuts {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 192px;
	margin: 0 32px 42px;
	border: 1px solid #efefef;
	border-radius: 20px;
	background: #ffffff;
}

.drawer-shortcuts-row {
	display: flex;
	align-items: flex-start;
	justify-content: center;
	gap: 62px;
	width: 100%;
	padding-top: 32px;
}

.drawer-shortcut {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	border: none;
	background: transparent;
	padding: 0;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	&__icon {
		width: 32px;
		height: 32px;
	}

	span {
		font-family: "Source Han Serif CN", serif;
		font-size: 24px;
		font-weight: 400;
		line-height: 40px;
		color: #333333;
		white-space: nowrap;
	}
}

.drawer-version {
	margin: 10px 0 0;
	text-align: center;
	font-family: "Source Han Serif CN", serif;
	font-size: 20px;
	font-weight: 400;
	line-height: normal;
	color: #999999;
}
</style>

<style lang="scss">
.m-user-center-drawer-popup.van-popup--left {
	transform: none;
}

.m-user-center-drawer-overlay {
	position: fixed;
}

.m-user-center-drawer-popup--en {
	.drawer-shortcuts {
		height: auto;
		min-height: 192px;
		margin-right: 16px;
		margin-left: 16px;
		padding: 0 0 12px;
		box-sizing: border-box;
	}

	.drawer-shortcuts-row {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
		width: 100%;
		padding: 28px 10px 0;
		box-sizing: border-box;
	}

	.drawer-shortcut {
		width: 100%;
		min-width: 0;
		justify-content: flex-start;
	}

	.drawer-shortcut span {
		display: block;
		width: 100%;
		min-width: 0;
		font-family: "Segoe UI", Arial, sans-serif;
		font-size: 16px;
		line-height: 1.2;
		text-align: center;
		white-space: normal;
		overflow-wrap: anywhere;
		word-break: normal;
	}
}

.van-dialog.seer-logout-dialog {
	width: calc(100vw - 24px);
	max-width: 560px;
	padding: 48px 45px 48px;
	border-radius: 16px;
}

.van-dialog.seer-logout-dialog .van-dialog__header {
	padding: 0;
	margin: 0 0 32px;
	font-family: "PingFang TC", "PingFang SC", sans-serif;
	font-size: 36px;
	font-weight: 600;
	line-height: 36px;
	color: #111111;
	text-align: center;
}

.van-dialog.seer-logout-dialog .van-dialog__footer {
	display: flex;
	gap: 20px;
	padding: 0;
}

.van-dialog.seer-logout-dialog .van-button {
	flex: 1;
	height: 72px;
	margin: 0;
	border-radius: 8px;
	font-family: "PingFang TC", "PingFang SC", sans-serif;
	font-size: 36px;
	font-weight: 600;
	line-height: 36px;
}

.van-dialog.seer-logout-dialog .van-dialog__cancel {
	color: #585858;
	background: #ffffff;
	border: 1px solid #dfdfdf;
}

.van-dialog.seer-logout-dialog .van-dialog__confirm {
	color: #ffffff;
	background: #111111;
	border: none;
}

.van-dialog.seer-logout-dialog .van-dialog__cancel .van-button__text {
	color: #585858 !important;
}

.van-dialog.seer-logout-dialog .van-dialog__confirm .van-button__text {
	color: #ffffff !important;
}
</style>
